import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { favoriteService } from "../api/services";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [machineIds, setMachineIds] = useState(new Set());
  const [fincaIds,   setFincaIds]   = useState(new Set());

  useEffect(() => {
    if (!user) {
      setMachineIds(new Set());
      setFincaIds(new Set());
      return;
    }
    favoriteService.ids().then(d => {
      setMachineIds(new Set(d.machineIds ?? []));
      setFincaIds(new Set(d.fincaIds ?? []));
    }).catch(() => {});
  }, [user]);

  const toggle = useCallback(async (id, type = "machine") => {
    const setIds = type === "finca" ? setFincaIds : setMachineIds;
    const isFav  = type === "finca" ? fincaIds.has(id) : machineIds.has(id);

    // Optimistic update
    setIds(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(id); else next.add(id);
      return next;
    });

    try {
      if (type === "finca") {
        await favoriteService.toggleFinca(id);
      } else {
        await favoriteService.toggleMachine(id);
      }
    } catch {
      // Revert on error
      setIds(prev => {
        const next = new Set(prev);
        if (isFav) next.add(id); else next.delete(id);
        return next;
      });
    }
  }, [machineIds, fincaIds]);

  const isFavorited = useCallback((id, type = "machine") =>
    type === "finca" ? fincaIds.has(id) : machineIds.has(id),
  [machineIds, fincaIds]);

  return (
    <FavoritesContext.Provider value={{ toggle, isFavorited, machineIds, fincaIds }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
};
